/**
 * Centralized display labels and UI copy as const objects for consistency and i18n readiness.
 */

/** Primary (top) navbar nav items */
export const PrimaryNavLabel = {
  Planning: "Planning",
  Value: "Value",
  ImoSummary: "IMO summary",
  SteerCo: "Steer co",
} as const;

export type PrimaryNavLabel = (typeof PrimaryNavLabel)[keyof typeof PrimaryNavLabel];

/** Deal phase filter values (for select value attribute) */
export const DealPhaseValue = {
  All: "all",
  PreSign: "pre-sign",
  SigningToClose: "signing-to-close",
  Day1: "day-1",
  First100Days: "first-100-days",
} as const;

export type DealPhaseValueType = (typeof DealPhaseValue)[keyof typeof DealPhaseValue];

/** Deal phase filter display labels */
export const DealPhaseLabel = {
  All: "All phases",
  PreSign: "Pre-sign",
  SigningToClose: "Signing to close",
  Day1: "Day 1",
  First100Days: "First 100 days",
} as const;

export type DealPhaseLabel = (typeof DealPhaseLabel)[keyof typeof DealPhaseLabel];

/** Sidebar nav item labels */
export const SidebarNavLabel = {
  Checklist: "Day 1 readiness checklist",
  IntegrationPlanning: "Integration planning",
  Raid: "RAID",
  RaidSubtitle: "Risks, assumptions, issues and dependencies",
  DecisionsLog: "Decisions log",
} as const;

/** "Back to" link target label (e.g. Back to Home) */
export const BackToLabel = {
  Home: "Home",
} as const;

export type SidebarNavLabel = (typeof SidebarNavLabel)[keyof typeof SidebarNavLabel];

/** Bottom-right AI chat quick menu copy */
export const ChatbotMenuLabel = {
  MenuTitle: "Ask Carter AI",
  OpenAiChat: "Open chat",
  CloseMenu: "Close",
} as const;

export type ChatbotMenuLabel = (typeof ChatbotMenuLabel)[keyof typeof ChatbotMenuLabel];

/** Footer legal link labels */
export const LegalLinkLabel = {
  LegalNotices: "Legal notices",
  Privacy: "Privacy",
  CookiePolicy: "Cookie policy",
  LegalDisclaimer: "Legal disclaimer",
  TermsAndConditions: "Terms and conditions",
} as const;

/** Footer copyright and legal copy */
export const FooterCopy = {
  Copyright: "PwC. All rights reserved.",
  LegalEntity:
    "PwC refers to the PwC network and/or one or more of its member firms, each of which is a separate legal entity.",
  StructurePrompt: "Please see",
  StructureLinkText: "pwc.com/structure",
  StructureSuffix: "for further details.",
} as const;

/** Footer external URL (PwC structure) */
export const FooterUrl = {
  PwcStructure: "https://www.pwc.com/structure",
} as const;

export type LegalLinkLabel = (typeof LegalLinkLabel)[keyof typeof LegalLinkLabel];

/** Profile/user menu items */
export const ProfileMenuLabel = {
  Settings: "Settings",
  Logout: "Logout",
  SignedInUser: "Signed in user",
} as const;

export type ProfileMenuLabel = (typeof ProfileMenuLabel)[keyof typeof ProfileMenuLabel];

/** Brand and app identity */
export const BrandLabel = {
  AppName: "Integration Insight Engine",
  LogoAlt: "Integration Insight Engine",
} as const;

export type BrandLabel = (typeof BrandLabel)[keyof typeof BrandLabel];

/** Login / sign-in screen copy */
export const LoginLabel = {
  PwcLogoAlt: "PwC",
  WelcomeTo: "Welcome to",
  ProductName: "Integration Insight Engine",
  SsoLogin: "SSO Login",
  Login: "Login",
  SignInFailed: "Sign-in failed:",
  AuthNotConfigured:
    "Sign-in is not configured for this environment. Set the VITE_AZUREAD_* variables.",
  Language: "English",
} as const;

export type LoginLabel = (typeof LoginLabel)[keyof typeof LoginLabel];

/** Logout / sign-out screen copy */
export const LogoutLabel = {
  SigningOut: "Signing you out…",
  Wait: "Please wait while we end your session.",
  SignOutFailed: "Sign-out failed:",
} as const;

export type LogoutLabel = (typeof LogoutLabel)[keyof typeof LogoutLabel];

/** Dropdown / form labels in layout */
export const LayoutLabel = {
  DealPhase: "Deal phase:",
} as const;

export type LayoutLabel = (typeof LayoutLabel)[keyof typeof LayoutLabel];

/** Shared UI default copy */
export const UiLabel = {
  Loading: "Loading...",
  SelectPlaceholder: "Select...",
} as const;

export type UiLabel = (typeof UiLabel)[keyof typeof UiLabel];

/** Error boundary fallback UI */
export const ErrorFallbackLabel = {
  Title: "Something went wrong",
  Description: "An unexpected error occurred. You can try again or return to the app.",
  TryAgain: "Try again",
} as const;

export type ErrorFallbackLabel = (typeof ErrorFallbackLabel)[keyof typeof ErrorFallbackLabel];

/** UI / Component examples page (design-phase demos) */
export const ExamplesLabel = {
  PageTitle: "Component examples",
  PageSubtitle: "Form, EmptyState, Modal, Spinner, and Input usage for reference.",
  FormExampleTitle: "Form example",
  FormExampleSubtitle: "HookFormField + Input + zod + react-hook-form.",
  FieldName: "Name",
  FieldDescription: "Description (optional)",
  Submit: "Save",
  ListExampleTitle: "Simple list (EmptyState + Modal + Spinner)",
  ListExampleSubtitle: "Empty list shows EmptyState; Add opens Modal with form; initial load shows Spinner.",
  EmptyListTitle: "No items yet",
  EmptyListDescription: "Add an item to get started.",
  AddItem: "Add item",
  ModalAddTitle: "Add item",
  ModalCancel: "Cancel",
  ModalSaving: "Saving...",
  LoadingList: "Loading list...",
} as const;

export type ExamplesLabel = (typeof ExamplesLabel)[keyof typeof ExamplesLabel];
